import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
// Import the ShadCN Slider
import { Textarea } from '@/components/ui/textarea';
import useScenarioTypes from '@/hooks/useScenarioTypes';
import { IScenario } from '@/types';
import axiosClient from '@/utils/axiosClient';

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  agent_name: z.string().min(1, { message: 'Agent Name is required' }),
  language: z.string().min(1, { message: 'Language is required' }),
  objective: z.string().min(1, { message: 'Objective is required' }),
  prompt: z.string().min(1, { message: 'Prompt is required' }),
  voice: z.string().min(1, { message: 'Voice is required' }),
  scenario_type_id: z.string().min(1, { message: 'Type is required' }),
  inbound: z.boolean().default(false),
  potential_objections: z.string().optional(),
  tips: z.string().optional(),
  metadata: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  scripts: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  scoring: z.string().optional(),
  first_sentence: z.string().optional(),

  // New slider field:
  interruption_threshold: z.number().min(50).max(200).default(50),
});

function mapAgentToFormValues(agent: IScenario) {
  return {
    name: agent.name || '',
    description: agent.description || '',
    agent_name: agent.agent_name || '',
    language: agent.language || '',
    objective: agent.objective || '',
    prompt: agent.prompt || '',
    voice: agent.voice || '',
    scenario_type_id: agent.scenario_type_id || '',
    inbound: agent.inbound || false,
    potential_objections: agent.potential_objections || '',
    tips: agent.tips || '',
    first_sentence: agent.first_sentence || '',
    scoring: agent.scoring ? JSON.stringify(agent.scoring, null, 2) : '',
    metadata: agent.metadata
      ? Object.keys(agent.metadata).map((key) => ({
          key,
          value: agent.metadata[key],
        }))
      : [],
    scripts: agent.scripts
      ? Object.keys(agent.scripts).map((key) => ({
          key,
          value: agent.scripts[key],
        }))
      : [],

    // Default to 50 if none is set
    interruption_threshold: agent.interruption_threshold || 50,
  };
}

interface AgentFormProps {
  scenario?: IScenario;
  onClose: () => void;
}

const ScenarioForm = ({ scenario, onClose }: AgentFormProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scenarioTypeModel = useScenarioTypes();
  const scenarioTypes = useMemo(() => scenarioTypeModel.data || [], [scenarioTypeModel.data]);
  const [defaultScoring, setDefaultScoring] = useState('');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      agent_name: '',
      language: '',
      objective: '',
      prompt: '',
      voice: '',
      potential_objections: '',
      tips: '',
      metadata: [],
      scripts: [],
      scoring: '',
      first_sentence: '',
      scenario_type_id: '',
      interruption_threshold: 50,
    },
  });

  useEffect(() => {
    if (scenario?.scoring) {
      form.setValue('scoring', JSON.stringify(scenario.scoring, null, 2));
    } else {
      const getDefaultScoring = async () => {
        try {
          const response = await axiosClient.get('v1/scenarios/default-scoring');
          const formattedJson = JSON.stringify(response.data, null, 2);
          setDefaultScoring(formattedJson);
          form.setValue('scoring', formattedJson);
        } catch (error) {
          console.error('Error loading default scoring:', error);
        }
      };
      getDefaultScoring();
    }
  }, [scenario, form]);

  const { control, handleSubmit } = form;

  const {
    fields: metadataFields,
    append: appendMetadata,
    remove: removeMetadata,
  } = useFieldArray({
    control,
    name: 'metadata',
  });

  const {
    fields: scriptsFields,
    append: appendScripts,
    remove: removeScripts,
  } = useFieldArray({
    control,
    name: 'scripts',
  });

  useEffect(() => {
    if (scenario) {
      form.reset(mapAgentToFormValues(scenario) as any);
    }
  }, [scenario, scenarioTypes]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      // Transform metadata into an object
      const metadata: Record<string, string> = {};
      if (data.metadata) {
        data.metadata.forEach((item) => {
          metadata[item.key] = item.value;
        });
      }

      // Transform scripts into an object
      const scripts: Record<string, string> = {};
      if (data.scripts) {
        data.scripts.forEach((item) => {
          scripts[item.key] = item.value;
        });
      }

      // Validate scoring JSON string but don't parse it
      if (data.scoring) {
        try {
          JSON.parse(data.scoring);
        } catch {
          form.setError('scoring', {
            type: 'manual',
            message: 'Invalid JSON format',
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare payload
      const payload = {
        name: data.name,
        description: data.description,
        agent_name: data.agent_name,
        language: data.language,
        objective: data.objective,
        prompt: data.prompt,
        voice: data.voice,
        potential_objections: data.potential_objections,
        tips: data.tips,
        first_sentence: data.first_sentence,
        scenario_type_id: data.scenario_type_id,
        inbound: data.inbound,
        metadata,
        scripts,
        scoring: data.scoring,

        // Include the new threshold
        interruption_threshold: data.interruption_threshold,
      };

      if (scenario) {
        await axiosClient.put(`v1/scenarios/${scenario.id}`, payload);
      } else {
        await axiosClient.post('v1/scenarios', payload);
      }

      queryClient.invalidateQueries('v1/scenarios');
      onClose();
    } catch (error) {
      console.error('Error saving scenario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <Accordion type="single" collapsible defaultValue="basicInfo">
          {/* Basic Information */}
          <AccordionItem value="basicInfo">
            <AccordionTrigger>Basic Information</AccordionTrigger>
            <AccordionContent>
              <div className="flex w-full gap-8">
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Scenario Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Name this scenario" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="scenario_type_id"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                          <SelectContent>
                            {scenarioTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex w-full gap-8 my-2">
                <FormField
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Short description of this scenario" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex w-full gap-8 my-2">
                <FormField
                  control={control}
                  name="agent_name"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Agent Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Name of the AI bot" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="language"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>AI Language</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a language" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en-US">English</SelectItem>
                            <SelectItem value="es-ES">Spanish</SelectItem>
                            {/* add more as needed */}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex w-full gap-8">
                <FormField
                  control={control}
                  name="voice"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>AI Voice</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a voice" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2f9fdbc7-4bf2-4792-8a18-21ce3c93978f">Maya</SelectItem>
                            <SelectItem value="37b3f1c8-a01e-4d70-b251-294733f08371">Ryan</SelectItem>
                            <SelectItem value="90295ec4-f0fe-4783-ab33-8b997ddc3ae4">Mason</SelectItem>
                            <SelectItem value="bbeabae6-ec8d-444f-92ad-c8e620d3de8d">Tina</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="inbound"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Call Direction</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={(value) => field.onChange(value === 'inbound')}
                          value={field.value ? 'inbound' : 'outbound'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select call direction" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inbound">Inbound</SelectItem>
                            <SelectItem value="outbound">Outbound</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {form.watch('inbound') && (
                  <FormField
                    control={control}
                    name="first_sentence"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>First Sentence</FormLabel>
                        <FormControl>
                          <Input placeholder="What should the bot say first?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* New Slider for interuption_threshold */}
              <div className="my-4">
                <FormField
                  control={control}
                  name="interruption_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interuption Threshold</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Slider
                            value={[field.value]}
                            onValueChange={(val) => field.onChange(val[0])}
                            min={50}
                            max={200}
                            step={1}
                            className="w-2/3"
                          />
                          <span className="w-12 text-right">{field.value}ms</span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name="objective"
                render={({ field }) => (
                  <FormItem className="w-full mt-4">
                    <FormLabel>Call Objective</FormLabel>
                    <FormControl>
                      <Textarea placeholder="What is the call objective?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prompt</FormLabel>
                    <FormControl>
                      <Textarea placeholder="What is the prompt for this agent?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Potential Objections */}
          <AccordionItem value="potentialObjections">
            <AccordionTrigger>Potential Objections</AccordionTrigger>
            <AccordionContent>
              <FormField
                control={control}
                name="potential_objections"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Potential Objections</FormLabel>
                    <FormControl>
                      <Textarea placeholder="List any potential objections" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Tips */}
          <AccordionItem value="tips">
            <AccordionTrigger>Tips</AccordionTrigger>
            <AccordionContent>
              <FormField
                control={control}
                name="tips"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tips</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Any tips?" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Scripts */}
          <AccordionItem value="scripts">
            <AccordionTrigger>Scripts</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <FormLabel>Define Steps and Scripts</FormLabel>
                  <Button type="button" onClick={() => appendScripts({ key: '', value: '' })}>
                    Add Step
                  </Button>
                </div>
                {scriptsFields.map((item, index) => (
                  <div key={item.id} className="flex items-start space-x-4">
                    <FormField
                      control={control}
                      name={`scripts.${index}.key`}
                      render={({ field }) => (
                        <FormItem className="w-1/3">
                          <FormLabel>Step Name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Opening" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={`scripts.${index}.value`}
                      render={({ field }) => (
                        <FormItem className="w-2/3">
                          <FormLabel>Script</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Enter the script for this step" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="button" variant="destructive" onClick={() => removeScripts(index)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Scoring */}
          <AccordionItem value="scoring">
            <AccordionTrigger>Scoring</AccordionTrigger>
            <AccordionContent>
              <FormField
                control={control}
                name="scoring"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scoring Configuration JSON</FormLabel>
                    <FormControl>
                      <Textarea
                        className="font-mono"
                        placeholder="Loading scoring rules..."
                        {...field}
                        value={field.value || defaultScoring}
                        rows={15}
                        disabled={!scenario?.can_edit_scoring}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Metadata */}
          <AccordionItem value="metadata">
            <AccordionTrigger>Data Points</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <FormLabel>Add any data points to the call that you need</FormLabel>
                  <Button type="button" onClick={() => appendMetadata({ key: '', value: '' })}>
                    Add Row
                  </Button>
                </div>
                {metadataFields.map((item, index) => (
                  <div key={item.id} className="flex items-center space-x-4">
                    <FormField
                      control={control}
                      name={`metadata.${index}.key`}
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormControl>
                            <Input placeholder="Key" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={`metadata.${index}.value`}
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormControl>
                            <Input placeholder="Value" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="button" variant="destructive" onClick={() => removeMetadata(index)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Button type="submit" disabled={isSubmitting} className="mt-4">
          <Save />
          {isSubmitting ? 'Saving...' : 'Save Scenario'}
        </Button>
      </form>
    </Form>
  );
};

export default ScenarioForm;
